import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import usePermissions from '../hooks/usePermissions';

// Route guard: blocks a STAFF user from a page their sub-role doesn't include.
//
// The fourth guard in the set, and the one that answers a different question
// from the other three:
//   PrivateRoute      are you signed in?
//   RoleRoute         is this your kind of account?
//   FeatureGuard      is this in the DOCTOR's subscription plan?
//   PermissionGuard   does your sub-role grant this?
//
// Admins pass unconditionally — they are who grants the permission. Anyone who
// is not staff at all passes too, because this guard has nothing to say about
// them; whichever of the guards above applies has already had its turn.
//
// This is the polite half only. Every route behind it is enforced server-side by
// api/middleware/requirePermission.js, so a user who edits localStorage reaches
// a page that returns 403 on everything it tries.
export default function PermissionGuard({ perm, children }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { can, ready, allowAll, isStaff } = usePermissions();

  if (allowAll || !isStaff) return children || <Outlet />;

  // Wait for the first resolve rather than flashing "no access" at somebody who
  // has it — the cached set is empty on a brand-new browser.
  if (!ready) {
    return (
      <div className="w-full flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (can(perm)) return children || <Outlet />;

  return (
    <div className="w-full flex items-center justify-center py-16 px-4" data-testid={`permission-locked-${perm}`}>
      <div className="bg-white rounded-2xl shadow-md max-w-md w-full p-8 text-center">
        <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center">
          <Lock className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-bold text-[#1a365d] mb-2">{t('featureLock.permission_title')}</h2>
        <p className="text-sm text-slate-500 mb-6">{t('featureLock.permission_message')}</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          data-testid={`permission-locked-cta-${perm}`}
          className="w-full px-4 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg"
        >
          {t('featureLock.permission_cta')}
        </button>
      </div>
    </div>
  );
}
